import { Box } from "@mui/material";
import { useEffect, useMemo, useState, useRef } from "react";
import AgGrid from "../components/grid/AgGrid";
import { defaultColDef, floatFormater, leftAlignColDef, preciseFloatFormater, rightAlignColDef } from "../components/grid/columnDefinitions/columnDefinitions";
import { useContextProvider } from "../components/page-context/ContextProvider";
import { Circularity } from "../db_access/circularity";
import {
  calculate_eol_feedstock_waste,
  calculate_eol_recycling_waste,
  calculate_eol_unrecoverable_waste,
  calculate_om_landfill_waste,
  calculate_om_waste_content,
  recalculate_row_mci,
} from "../functions/CircularityFunctions";

import ColumnMenu from "../components/context-menu/ColumnMenu";

function EndOfLifeTreatmentTable({ circularities, columnMenuVisible, setColumnMenuVisible, anchorRef, setCircularities, topic = "Product" }) {
  const { isSaveClicked, setIsSaveVisible, setRefresh, setIsLoading, setIsSaveClicked, columnVisibilities } = useContextProvider();
  const [endOfLifes, setEndOfLifes] = useState([]);
  const [modified, setModified] = useState(false);
  const gridRef = useRef(null);

  const colVisibility_key = "circularity_eol_treatment_table_visibility";

  const rawColDefs = [
    {
      headerName: "SI No.",
      valueGetter: "node.rowIndex + 1",
      width: 80,
      minWidth: 60,
      headerClass: "ag-center-aligned-header",
      cellStyle: { display: "flex", alignItems: "center", justifyContent: "center" },
      suppressMenu: true,
      sortable: false,
      field: "si_no"
    },
    { field: "description", headerName: "Description", ...leftAlignColDef, ...defaultColDef, flex: 1 },
    { field: "eol_recycling_efficiency", headerName: "Recycling Efficiency After Use (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater, editable: true },
    {
      field: "eol_feedstock_efficiency",
      headerName: `Recycling Efficiency of Recycled Material used in the ${topic} (%)`,
      ...rightAlignColDef,
      ...defaultColDef,
      ...floatFormater,
      editable: true,
    },
    { field: "eol_recycling_waste", headerName: `Waste from Recycling ${topic} (% of total mass)`, ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "eol_feedstock_waste", headerName: `Waste from Recycled Material used in the ${topic} (% of total mass)`, ...rightAlignColDef, ...defaultColDef, ...floatFormater },
    { field: "eol_unrecoverable_waste", headerName: "Total Unrecoverable Waste (%)", ...rightAlignColDef, ...defaultColDef, ...floatFormater },


  ];


  const defaultCols = useMemo(() => rawColDefs.map((c) => c.field), []);

  const columnDefs = useMemo(() => {
    const isHidden = (field) => {
      if (field === "si_no") return false;
      return !columnVisibilities[colVisibility_key]?.includes(field);
    };
    return rawColDefs.map((col) => ({
      ...col,
      hide: isHidden(col.field),
    }));
  }, [columnVisibilities, colVisibility_key, topic]);

  useEffect(() => {
    if (modified || isSaveClicked) return; // Don't sync if we have local changes or are saving
    const updated = circularities.map(eol => {
      const new_eol = { ...eol };
      if (new_eol.eol_recycling_efficiency === null || new_eol.eol_recycling_efficiency === undefined) new_eol.eol_recycling_efficiency = 90;
      if (new_eol.eol_feedstock_efficiency === null || new_eol.eol_feedstock_efficiency === undefined) new_eol.eol_feedstock_efficiency = 90;

      
      if (new_eol.eol_recycling_efficiency !== eol.eol_recycling_efficiency || new_eol.eol_feedstock_efficiency !== eol.eol_feedstock_efficiency) {
        new_eol.eol_recycling_waste = calculate_eol_recycling_waste(new_eol);
        new_eol.eol_feedstock_waste = calculate_eol_feedstock_waste(new_eol);
        new_eol.om_waste_content = calculate_om_waste_content(new_eol);
        new_eol.om_landfill_waste = calculate_om_landfill_waste(new_eol);
        new_eol.eol_unrecoverable_waste = calculate_eol_unrecoverable_waste(new_eol);
        // Removed setModified(true) to prevent loop
      }
      return new_eol;
    });
    setEndOfLifes(updated);
  }, [circularities, modified, isSaveClicked]);




  useEffect(() => {
    if (isSaveClicked && modified) {
      const payload = endOfLifes.map((item) => ({ ...item, id: Number(item.id) }));
      Circularity.update_end_of_lifes_materials(payload).finally(() => {
        setModified(false);
        // Parent handles setIsLoading and setIsSaveClicked after the 2s MCI sync
      });

    }
  }, [isSaveClicked]);

  useEffect(() => {
    if (modified) {
      setIsSaveVisible(true);
    }
  }, [modified]);

  const onCellValueChanged = (params) => {
    const { data, colDef, value } = params;
    let validatedValue = value;
    if (["eol_recycling_efficiency", "eol_feedstock_efficiency"].includes(colDef.field)) {
      const parsed = parseFloat(value);
      validatedValue = isNaN(parsed) ? 0 : Math.max(0, Math.min(100, parsed));
    }
    const updated = endOfLifes.map((eol) => {
      if (eol.id === data.id) {
        const new_eol = { ...eol, [colDef.field]: validatedValue };
        return recalculate_row_mci(new_eol);
      } else {
        return eol;
      }
    });
    setEndOfLifes(updated);
    setModified(true);
    // Notify parent for live summary updates
    if (setCircularities) {
      setCircularities(updated);
    }
  };

  const setDefaultRecycledValues = () => {
    const updatedData = endOfLifes.map((eol) => {
      const new_eol = { ...eol };
      new_eol.eol_recycling_efficiency = 90;
      new_eol.eol_feedstock_efficiency = 90;
      new_eol.eol_recycling_waste = calculate_eol_recycling_waste(new_eol);
      new_eol.eol_feedstock_waste = calculate_eol_feedstock_waste(new_eol);
      new_eol.om_waste_content = calculate_om_waste_content(new_eol);
      new_eol.om_landfill_waste = calculate_om_landfill_waste(new_eol);
      new_eol.eol_unrecoverable_waste = calculate_eol_unrecoverable_waste(new_eol);
      return new_eol;
    });
    setEndOfLifes(updatedData);
    setModified(true);
  };

  return (
    <Box sx={{ height: "auto" }}>
      <AgGrid 
        ref={gridRef}
        columnDefs={columnDefs} 
        rowData={endOfLifes} 
        onCellValueChanged={onCellValueChanged} 
        headerCheckbox={false} 
      />
      <ColumnMenu
        anchorRef={anchorRef}
        visible={columnMenuVisible}
        onClose={() => setColumnMenuVisible(false)}
        colDefs={rawColDefs}
        colVisibility_key={colVisibility_key}
        defaultCols={defaultCols}
        gridRef={gridRef}
      />
    </Box>
  );
}

export default EndOfLifeTreatmentTable;

